/**
 * The subcontractor page: one link per sub per job.
 *
 * He gets no login and no account. The office enters everything, he reads it,
 * taps "This looks right" and signs waivers. Nothing on the page is news to
 * him: his agreed total, what has been paid, the milestones still ahead, the
 * scope he agreed to, the invoices we hold from him, and his waivers.
 *
 * Deliberately NOT on the page: his insurance and licence expiry. That was
 * considered and left off. It stays an internal alert.
 *
 * THE STANDING RULE FOR EVERYTHING IN THIS FILE AND ITS NEIGHBOURS: nothing
 * here is ever a gate. If he never opens the link, never agrees and never
 * signs, Friday still pays him.
 */

export type SubMilestone = {
  id: string;
  seq: number;
  milestone: string;
  amount: number;
  hold_until_punch_clear: boolean;
  paid: boolean;
};

export type SubInvoiceRow = {
  id: string;
  invoice_number: string | null;
  invoice_date: string;
  amount: number;
};

export type SubWaiverRow = {
  id: string;
  form_code: string;
  title: string;
  amount: number;
  through_date: string | null;
  status: string;
  signed_at: string | null;
  /** False when the office has not pasted that form's statutory wording yet. */
  form_ready: boolean;
  /** False until the office has ticked the wording off against the official form. Never a gate. */
  form_verified: boolean;

};

export type SubPagePayload = {
  company: string;
  job_location: string;
  scope: string[];
  total: number;
  paid: number;
  balance: number;
  milestones: SubMilestone[];
  invoices: SubInvoiceRow[];
  waivers: SubWaiverRow[];
  /** The live schedule fingerprint, so a changed schedule asks again. */
  schedule_hash: string;
  agreed_at: string | null;
  /**
   * His jobsite acknowledgment, which is per SUBCONTRACTOR and not per job.
   * Whichever live link he holds can surface it, and signing it once satisfies
   * it everywhere. The lien waivers above stay per project and per payment.
   */
  acknowledgment: import("./sub-acknowledgment").SubAcknowledgmentState;
  office_phone: string;

  /**
   * Today's crew check-in on this job, when one is open. It carries the
   * close-out too, because once he is marked on site the evening question is
   * the only one left to answer.
   */
  crew_today: {
    id: string;
    state: string;
    closeout_note: string | null;
    has_photo: boolean;
    /** What he last said about coming back, including his finished claim. */
    return_plan: string | null;
    finished_claimed_at: string | null;
  } | null;
  /** Today's business date, so the day buttons are named off the office clock. */
  crew_today_date: string;
  /** Working days from settings, so a Saturday is never offered as a return day. */
  crew_working_days: number[];
  /** The job number, shown with the address above the buttons he taps. */
  job_code: string | null;

};


/**
 * A stable fingerprint of what he is being asked to agree to. Change the
 * total or any milestone and the fingerprint changes, which is what makes the
 * previous agreement superseded rather than silently still standing.
 */
export function scheduleFingerprint(total: number, milestones: SubMilestone[]): string {
  const parts = [
    total.toFixed(2),
    ...[...milestones]
      .sort((a, b) => a.seq - b.seq)
      .map(
        (m) =>
          `${m.seq}|${m.milestone.trim().toLowerCase()}|${Number(m.amount).toFixed(2)}|${m.hold_until_punch_clear ? "h" : ""}`,
      ),
  ];
  return parts.join("~");
}

export const WAIVER_TOKENS = [
  "{CLAIMANT}",
  "{CUSTOMER}",
  "{JOB_LOCATION}",
  "{OWNER}",
  "{THROUGH_DATE}",
  "{MAKER_OF_CHECK}",
  "{AMOUNT_OF_CHECK}",
  "{CHECK_PAYABLE_TO}",
] as const;

export type WaiverTokenValues = Partial<Record<string, string>>;

/**
 * Fills the office's pasted statutory text.
 *
 * A token with no value, or with an EMPTY value, is deliberately left standing
 * so the caller can see it was never filled. Printing `Owner:` with nothing
 * after it on a statutory document is worse than refusing to print it at all.
 */
export function fillWaiverText(body: string, values: WaiverTokenValues): string {
  return body.replace(/\{[A-Z_]+\}/g, (t) => {
    const v = values[t];
    return typeof v === "string" && v.trim() ? v : t;
  });
}

/** Tokens still standing in a filled body, in the order they appear, deduped. */
export function unresolvedTokens(filled: string): string[] {
  return Array.from(new Set(filled.match(/\{[A-Z_]+\}/g) ?? []));
}

/** The sentence shown when a statutory form would print an unfilled blank. */
export function waiverTokenRefusal(missing: string[]): string {
  return `This waiver cannot be prepared because ${missing.join(", ")} could not be filled in. A statutory form must never go out half filled. Please call the office.`;
}

/**
 * Fill, then refuse rather than hand a subcontractor a half-filled statutory
 * document. A typo in the pasted wording, or a missing owner name, both land
 * here instead of on a signed form.
 */
export function fillWaiverStrict(
  body: string,
  values: WaiverTokenValues,
): { ok: true; body: string } | { ok: false; missing: string[]; message: string } {
  const filled = fillWaiverText(body, values);
  const missing = unresolvedTokens(filled);
  if (missing.length) return { ok: false, missing, message: waiverTokenRefusal(missing) };
  return { ok: true, body: filled };
}

/**
 * Obviously-fake values, used ONLY to preview a pasted form on screen so the
 * office can see the blanks fill before they save. Never written anywhere.
 *
 * The amount carries NO dollar sign: the statutory body already prints one.
 */
export function waiverPreviewValues(legalName: string): Record<string, string> {
  return {
    "{CLAIMANT}": "Valley Plumbing Inc.",
    "{CUSTOMER}": legalName,
    "{JOB_LOCATION}": "1420 Oak Street, Anaheim, CA 92805",
    "{OWNER}": "Maria Gonzalez",
    "{THROUGH_DATE}": "31 July 2026",
    "{MAKER_OF_CHECK}": legalName,
    "{AMOUNT_OF_CHECK}": "4,250.00",
    "{CHECK_PAYABLE_TO}": "Valley Plumbing Inc.",
  };
}

export const WAIVER_PREVIEW_VALUES: Record<string, string> =
  waiverPreviewValues("Dreamliner Construction");


/** Known tokens the pasted text does not contain, so they would stay blank. */
export function missingWaiverTokens(body: string): string[] {
  return WAIVER_TOKENS.filter((t) => !body.includes(t));
}

/**
 * Text with no token at all is guaranteed to print a waiver with empty blanks,
 * which is the one case safe to refuse outright.
 */
export function hasAnyWaiverToken(body: string): boolean {
  return /\{[A-Z_]+\}/.test(body);
}


export const FORM_TITLES: Record<string, string> = {
  "8132": "Conditional waiver and release on progress payment",
  "8136": "Conditional waiver and release on final payment",
};

/**
 * The one sentence shown, and returned by the server, when a form's statutory
 * wording has not been pasted in yet. A blank or invented lien waiver is worse
 * than none, so sending and signing refuse outright.
 */
/**
 * Said out loud wherever a waiver is sent, signed or listed while the office
 * has not ticked that form off against the official published form. It is a
 * statement, never a gate: sending and signing carry on regardless.
 */
export const FORM_NOT_VERIFIED =
  "This wording has not been checked against the official California form yet.";

export const FORM_NOT_ON_FILE =
  "This waiver cannot be prepared yet because the official California wording for this form has not been entered in the Hub. Please call the office.";

/** The address line the sub already has on his paperwork, and nothing else. */
export function jobLocation(p: {
  address_line?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
}): string {
  return [p.address_line, p.city, [p.state, p.postal_code].filter(Boolean).join(" ")]
    .filter((x) => x && String(x).trim())
    .join(", ");
}

/**
 * The last real payment out to each subcontractor on a job.
 *
 * A waiver releases lien rights UP TO a date, and it is conditioned on ONE
 * cheque, so both the through date and the "Amount of Check" come from this
 * one payment, never from today's date or from everything paid so far. Voided
 * payments are not payments.
 */
export function lastPaymentBySub(
  rows: { subcontractor_id?: string | null; paid_on?: string | null; amount?: number | null; voided_at?: string | null }[],
): Map<string, { paidOn: string; amount: number }> {
  const out = new Map<string, { paidOn: string; amount: number }>();
  for (const p of rows ?? []) {
    if (!p.subcontractor_id || p.voided_at || !p.paid_on) continue;
    const seen = out.get(p.subcontractor_id);
    if (!seen || p.paid_on > seen.paidOn) {
      out.set(p.subcontractor_id, { paidOn: p.paid_on, amount: Number(p.amount ?? 0) });
    }
  }
  return out;
}
